import { useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Divider, Descriptions, Typography, Button, Modal } from 'antd';
import { useAppDispatch, useAppSelector } from '../../methods/redux/hooks';
import { shortAddress, commas } from '../../methods/utils/general-utils';
import ProcessInAppRequest from './ProcessInAppRequest';
import ProcessRequest from './ProcessRequest';
import ReverseFunds from './ReverseFunds';
import ReverseCompletedWithdrawal from './ReverseCompletedWithdrawal';
import ChangeWithdrawalStatus from './ChangeWithdrawalStatus';
import { MyCard } from '../../users/users-list/by-usertype/ByUserType';
import { getWithdrawalRequests } from '../../methods/redux/reducers/withdrawal-requests';
import SingleWithdrawalRequestPayoutStatus from './SingleWithdrawalRequestPayoutStatus';

const MainPage = styled.div`
	display: flex;
	flex-direction: column;
	border-radius: 10px;

	& > div {
		display: flex;
		flex-direction: column;
		margin-right: 15px;

		& .label {
			color: #aaa;
			font-style: italic;
			font-size: 10px;
		}
		& .value {
			font-size: 16px;
		}
	}

	& .details {
		padding: 15px 0;

		& > * {
			display: inline-block;
			min-width: 200px;
			margin-bottom: 7px;
			padding: 5px;
		}

		& .label {
			font-size: 13px;
			color: #808080;
			font-style: italic;
		}
	}
`;

const { Text } = Typography;

const SingleWithdrawalRequest = () => {
	const dispatch = useAppDispatch();
	const { itemsList } = useAppSelector((store) => store.withdrawalRequests);
	const [showPayoutModal, setShowPayoutModal] = useState(false);
	const [modalRequestId, setModalRequestId] = useState<string | null>(null);

	const [searchParams] = useSearchParams();
	const getQueryParams = () => Object.fromEntries(searchParams.entries());
	const { requestId } = getQueryParams();

	useEffect(() => {
		const queryData = Object.fromEntries(searchParams.entries());
		if (Object.keys(queryData).length > 0) {
			dispatch(getWithdrawalRequests(queryData));
		}
	}, [searchParams]);

	const interpretStatus = (statusValue: any) => {
		switch (statusValue) {
			case '0':
				return 'Pending';
			case '1':
				return 'Completed';
			case '2':
				return 'Failed';
			case '3':
				return 'Cancelled';
			case '4':
				return 'Deleted';
			case '5':
				return 'Processing';
			case '6':
				return 'Retry';
			case '7':
				return 'On-Hold';
			case '8':
				return 'Review';
			case '9':
				return 'Refunded';
			default:
				return 'Unknown';
		}
	};

	return (
		<MyCard
			title={`Withdrawal Request - ${requestId}`}
			style={{ margin: '20px' }}
		>
			<MainPage>
				{itemsList.map((item: any) => {
					const statusKey = String(item?.status);
					let statusColor = '#333';
					// status codes: '1' = Completed, '2' = Failed, '0' = Pending
					if (statusKey === '1' || statusKey.toLowerCase().includes('completed')) {
						statusColor = 'var(--green-color)';
					} else if (statusKey === '2' || statusKey.toLowerCase().includes('failed')) {
						statusColor = '#ff4d4f';
					} else if (statusKey === '0' || statusKey.toLowerCase().includes('pending')) {
						statusColor = '#faad14';
					}
					return (
					<div key={item?._id}>
						<Descriptions bordered column={2}>
							<Descriptions.Item label="Username">
								<Text strong>
									@
									<Link
										title="View Profile"
										to={`/user-profile/${item?.userId?._id}`}
									>
										{item?.userId?.username}
									</Link>
								</Text>
							</Descriptions.Item>

							<Descriptions.Item label="Final Amount">
								<Text strong>
									{commas(item?.finalAmount || 0)} ({item?.currencyId?.symbol})
								</Text>
							</Descriptions.Item>

							<Descriptions.Item label="Fee">
								<Text strong>
									{commas(item?.fee || 0)} ({item?.currencyId?.symbol})
								</Text>
							</Descriptions.Item>

							<Descriptions.Item label="Fee In Usd">
								<Text strong>${commas(item?.feeInUsd || 0)}</Text>
							</Descriptions.Item>

							<Descriptions.Item label="Discount">
								<Text strong>{commas(item?.discount || 0)}</Text>
							</Descriptions.Item>

							<Descriptions.Item label="Total Amount">
								<Text strong>
									{commas(item?.amount || '0')} ({item?.currencyId?.symbol})
								</Text>
							</Descriptions.Item>

							<Descriptions.Item label="Former Balance">
								<div style={{ display: 'flex' }}>
									<img
										alt="icon"
										src={item?.currencyId?.iconUrl}
										style={{ height: 20, width: 20, marginRight: 6 }}
									/>
									<Text strong>
										{commas(item?.formerBalance || 0)} (
										{item?.currencyId?.symbol})
									</Text>
								</div>
							</Descriptions.Item>

							<Descriptions.Item label="Crypto Balance">
								<Text strong>
									{commas(item?.cryptoBalanceId?.balance || 0)}
								</Text>
							</Descriptions.Item>
						</Descriptions>

						<br />
						<Divider />
						{item?.withdrawalType?.toLowerCase() === 'fiat' &&
							item?.paymentGatewayId && (
								<div
									style={{
										display: 'flex',
										justifyContent: 'flex-end',
										margin: '8px 0',
										gap: '10px',
									}}
								>
									<p> Payout Status: Completed</p>

									<Button
										size="small"
										type="default"
										onClick={() => {
											setModalRequestId(item?._id || null);
											setShowPayoutModal(true);
										}}
									>
										View {item?.paymentGatewayId?.gateWayKey} Payout Status
									</Button>
									<Modal
										visible={showPayoutModal}
										onCancel={() => setShowPayoutModal(false)}
										footer={null}
										width={900}
										destroyOnClose
										title={`Payout Request Id - ${item?._id}`}
									>
										{modalRequestId && (
											<SingleWithdrawalRequestPayoutStatus
												requestIdProp={modalRequestId}
											/>
										)}
									</Modal>
								</div>
							)}
						<br />

						<div className="details">
							<div>
								<p className="xendbrige-ref">
									<strong>
										{item?.withdrawalType ? item?.withdrawalType : '--'}
									</strong>
								</p>
								<p className="label">Withdrawal Type</p>
							</div>
							<div>
								<p className="xendbrige-ref">
									<span style={{ color: statusColor }}>
										{interpretStatus(String(item?.status))}
									</span>
								</p>
								<p className="label">Status</p>
							</div>

							{item?.withdrawalType.toLowerCase() !== 'fiat' && (
								<div>
									<a
										target="_blank"
										href={`https://${item?.blockchainNetworkId?.explorerUrl}/address/${item?.destinationAddress}#tokentxns`}
										rel="noreferrer"
									>
										{item?.destinationAddress &&
											shortAddress(item?.destinationAddress)}
									</a>
									<p className="label">
										Destination{' '}
										{item?.withdrawalAddressWhiteListId && '(whitelisted)'}
									</p>
								</div>
							)}
							{item?.withdrawalType.toLowerCase() !== 'fiat' && (
								<div>
									<p>{item?.blockchainNetworkId?.abbreviation}</p>
									<p className="label">Blockchain Network</p>
								</div>
							)}
							{item?.withdrawalType.toLowerCase() !== 'fiat' && (
								<div>
									{item?.transactionHash ? (
										<a
											href={`https://${item?.blockchainNetworkId?.explorerUrl}/tx/${item?.transactionHash}`}
											target="_blank"
											rel="noreferrer"
										>
											Link to Explorer
										</a>
									) : (
										'--'
									)}
									<p className="label">Hash</p>
								</div>
							)}
							{item?.withdrawalType.toLowerCase() === 'fiat' && (
								<div>
									<p>{item?.transactionHash ? item?.transactionHash : '--'}</p>

									<p className="label">Session Id</p>
								</div>
							)}

							<div>
								<p className="xendbrige-ref">
									{item?.p2pRef ? item?.p2pRef : '--'}
								</p>
								<p className="label">XendBridge Ref.</p>
							</div>
							{item?.withdrawalType.toLowerCase() !== 'fiat' && (
								<div>
									<p className="xendbrige-ref">
										{item?.blockchainMemo ? item?.blockchainMemo : ''}
									</p>
									<p className="label">BlockChain Memo</p>
								</div>
							)}
							<div>
								{item?.processingChannel === 3 && <p>--</p>}
								{item?.processingChannel === 2 && <p>Robot</p>}
								{item?.processingChannel === 1 && <p>Manual</p>}
								<p className="label">Processing Channel</p>
							</div>
							<div>
								<p>{item?.processedBy ? item?.processedBy?.email : '--'}</p>
								<p className="label">Processed By</p>
							</div>
							<div>
								<p>
									{item?.lastStatusChangedByUserId
										? item?.lastStatusChangedByUserId?.email
										: '--'}
								</p>
								<p className="label">Last Status Changed By</p>
							</div>
							<div>
								<p>
									{item?.processedAt
										? moment(item?.processedAt).format(
												'MMM DD, YY / hh:mm:ss a'
										  )
										: '--'}
								</p>
								<p className="label">Processed At</p>
							</div>
							<div>
								<p>{item?.discountApplied ? 'True' : 'False'}</p>
								<p className="label">Discount Applied</p>
							</div>
							<div>
								<p>{item?.gasPriceInNativeToken ?? '--'}</p>
								<p className="label">Gas Price In Native Token</p>
							</div>
							<div>
								<p>{item?.gasPriceInUsd ?? '--'}</p>
								<p className="label">Gas Price In Usd</p>
							</div>
							<div>
								<p>{item?.nativeTokenPrice ?? '--'}</p>
								<p className="label">Native Token Price</p>
							</div>
							<div>
								<p>{item?.sellTax ?? '--'}</p>
								<p className="label">Sell Tax</p>
							</div>
							{item?.inAppTransaction && (
								<div>
									<p>
										<span
											className="material-icons"
											style={{ color: 'var(--green-color)' }}
										>
											check
										</span>
									</p>
									<p className="label">InApp Transaction</p>
								</div>
							)}

							<div>
								<p>{item?.destinationPaymentDetailId?.paymentMethod ?? '--'}</p>
								<p className="label">Payment Method</p>
							</div>
							{/* {item?.withdrawalType?.toLowerCase() === 'fiat' &&
								item?.paymentGatewayId && (
									<>
										<Button
											type="default"
											onClick={() => {
												setModalRequestId(item?._id || null);
												setShowPayoutModal(true);
											}}
										>
											View {item?.paymentGatewayId?.gateWayKey} Payout Status
										</Button>
										<Modal
											visible={showPayoutModal}
											onCancel={() => setShowPayoutModal(false)}
											footer={null}
											width={900}
											destroyOnClose
											title={`Payout Status - ${item?._id}`}
										>
											{modalRequestId && (
												<SingleWithdrawalRequestPayoutStatus
													requestIdProp={modalRequestId}
												/>
											)}
										</Modal>
									</>
								)} */}
							<div>
								<p>{item?.destinationPaymentDetailId?.bankName ?? '--'}</p>
								<p className="label">Bank Name</p>
							</div>
							<div>
								<p>{item?.destinationPaymentDetailId?.accountName ?? '--'}</p>
								<p className="label">Account Name</p>
							</div>
							<div>
								<p>{item?.destinationPaymentDetailId?.accountNumber ?? '--'}</p>
								<p className="label">Account Number</p>
							</div>
							<div>
								<p>{item?.destinationPaymentDetailId?.sortCode ?? '--'}</p>
								<p className="label">Sort Code</p>
							</div>
							<div>
								<p>{item?.holdReason ?? '--'}</p>
								<p className="label">Hold Reason</p>
							</div>

							<br />

							{item?.fiatPaymentAccountDetails && (
								<div>
									<p>Fiat Payment Account Details</p>
									<div>
										<p>
											{item?.fiatPaymentAccountDetails?.accountName ?? '--'}
										</p>
										<p className="label">accountName</p>
									</div>

									<div>
										<p>{item?.fiatPaymentAccountDetails?.bankCode ?? '--'}</p>
										<p className="label">bankCode</p>
									</div>

									<div>
										<p>{item?.fiatPaymentAccountDetails?.bvn ?? '--'}</p>
										<p className="label">bvn</p>
									</div>

									<div>
										<p>{item?.fiatPaymentAccountDetails?.message ?? '--'}</p>
										<p className="label">message</p>
									</div>

									<div>
										<p>{item?.fiatPaymentAccountDetails?.sessionID ?? '--'}</p>
										<p className="label">sessionID</p>
									</div>

									<div>
										<p>
											{item?.fiatPaymentAccountDetails?.transactionId ?? '--'}
										</p>
										<p className="label">transactionId</p>
									</div>

									<div>
										<p>{item?.fiatPaymentAccountDetails?.kycLevel ?? '--'}</p>
										<p className="label">kycLevel</p>
									</div>

									<div>
										<p>
											{item?.fiatPaymentAccountDetails
												?.bankVerificationNumber ?? '--'}
										</p>
										<p className="label">bankVerificationNumber</p>
									</div>

									<div>
										<p>
											{item?.fiatPaymentAccountDetails?.channelCode ?? '--'}
										</p>
										<p className="label">channelCode</p>
									</div>
								</div>
							)}

							<br />

							<div>
								<p>
									{moment(item?.updatedAt).format('MMM DD, YYYY h:mm:ss a')}{' '}
								</p>
								<p>({moment(item?.updatedAt).fromNow()})</p>
								<p className="label">Updated</p>
							</div>
							<div>
								<p>
									{moment(item?.createdAt).format('MMM DD, YYYY h:mm:ss a')}
								</p>
								<p>({moment(item?.createdAt).fromNow()})</p>
								<p className="label">Created</p>
							</div>

							<div>
								{Number(item?.status) !== 1 && (
									<ChangeWithdrawalStatus request={item} />
								)}

								{Number(item?.status) >= 5 && Number(item?.status) <= 7 && (
									<>
										<ProcessRequest request={item} />

										<ProcessInAppRequest request={item} />
									</>
								)}
								{Number(item?.status) >= 6 && Number(item?.status) <= 8 && (
									<div>
										<ReverseFunds item={item} />
									</div>
								)}
								{/* To revert completed fiat transaction */}
								{item?.withdrawalType.toLowerCase() === 'fiat' &&
									Number(item.status) === 1 && (
										<div>
											<ReverseCompletedWithdrawal item={item} />
										</div>
									)}
							</div>
						</div>
					</div>
					)
				})}
			</MainPage>
		</MyCard>
	);
};

export default SingleWithdrawalRequest;
